import { useState, useRef, useEffect } from 'react';
import Modal from '@Common/Modal';
import { Info } from '@phosphor-icons/react';

interface MaskModalInputProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (maskPrompt: string) => void;
  isSubmitting?: boolean;
}

const MaskModalInput = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}: MaskModalInputProps) => {
  const [maskPrompt, setMaskPrompt] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      if (maskPrompt) {
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
      }
    }
  }, [maskPrompt]);

  useEffect(() => {
    if (isOpen) {
      setMaskPrompt('');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (maskPrompt.trim()) {
      onSubmit(maskPrompt.trim());
    }
  };

  const handleCancel = () => {
    setMaskPrompt('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} title="Outpainting Mask">
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-2 rounded-lg bg-blue-50 p-3">
          <Info size={20} className="mt-0.5 flex-shrink-0 text-blue-600" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Define the mask area</p>
            <p className="mt-1 text-xs">
              Describe what part of the image should be retained. For example:
              "background", "sky", "foreground", "left side", etc. Note: The
              more specific your description, the better the results. Due to
              model limitations, complex masks may not yield perfect results or
              result in an error.
            </p>
          </div>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Mask Description
          </label>
          <textarea
            ref={textareaRef}
            value={maskPrompt}
            onChange={(e) => setMaskPrompt(e.target.value)}
            placeholder="e.g., background, sky, entire background area..."
            className="max-h-[120px] min-h-[80px] w-full resize-none rounded-lg border border-gray-300 p-3 text-sm text-gray-700 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            disabled={isSubmitting}
          />
          <p className="mt-1 text-xs text-gray-500">
            {maskPrompt.length} / 512 characters
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={handleCancel}
            disabled={isSubmitting}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!maskPrompt.trim() || isSubmitting}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default MaskModalInput;
